import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Play, Square, Plus, TrendingUp, Plane, Calendar, Clock,
  AlertTriangle, Loader2, Coffee, Wrench, ChevronRight, ChevronDown, Trash2,
} from 'lucide-react';
import ManualEntryModal from '../components/ManualEntryModal';
import CorrectionRequestModal from '../components/CorrectionRequestModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { can } from '../lib/permissions';
import { useToast } from '../components/Toast';
import { FEEDBACK, isPendingApproval, eventNoun } from '../services/feedbackMessages';
import { canCancelOwnEvent, cancelWarningText } from '../utils/ownEventCancel';
import {
  createEvent, getEventsByRange, getWorkHours, deleteEvent,
  jsDowToBackend, buildGeoInfoWithMotivo,
  EVENT_TYPES, APPROVAL_STATUS, EVENT_TYPE_LABELS, getWorkplaces, getMe,
} from '../services/api';
import {
  GEO_ORIGIN, GEO_REQUIRED_TITLE, captureGeoInfo, geoRequiredMessage,
} from '../lib/geoInfo';
import { getBrowserPosition, resolveGeoMode } from '../services/browserLocation';
import { computeDayWork } from '../lib/hourBalance';
import { findOldestOpenClockDay } from '../lib/openClockDays';
import { filterEventsToLocalRange } from '../lib/dayStatus';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, zonedParts, formatHmInZone,
  minutesOfDayInZone, todayInZone, addDaysToDayKey, dowOfDayKey, zonedDateTimeToInstant,
} from '../lib/workerTime';

// Ventana de historial que descarga el panel. Sirve para dos cosas y por eso
// es una constante y no un número suelto: acota la petición de eventos Y acota
// hasta dónde se buscan jornadas abiertas sin cerrar. Si se separan, la
// detección buscaría en días de los que no hay datos descargados.
const EVENTS_WINDOW_DAYS = 30;

const greetingFor = (h) => {
  if (h < 6) return 'Buenas noches';
  if (h < 13) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
};

// Formatea una clave de día ('YYYY-MM-DD') sin que el navegador la desplace:
// se interpreta y se pinta en UTC (es una fecha de calendario, no un instante).
const fmtDayKey = (key, opts) => {
  try {
    return new Date(`${String(key).slice(0, 10)}T00:00:00Z`).toLocaleDateString('es-ES', { ...opts, timeZone: 'UTC' });
  } catch { return key; }
};

// Resumen por día. Trabajado y pausas salen de computeDayWork
// (src/lib/hourBalance.js): NINGUNA pausa cuenta como trabajada, todas se
// restan (decisión de producto 2026-09-21), igual que la bolsa y el resto de
// pantallas. Los fichajes rechazados no cuentan.
// `tz`: zona del trabajador — decide a qué día pertenece cada fichaje.
// `user`: para marcar qué fichajes del día puede ANULAR el propio trabajador
// (entradas/salidas suyas pendientes de aprobación, ver utils/ownEventCancel).
const groupEventsByDay = (events, tz = DEFAULT_TIME_ZONE, user = null) => {
  const map = new Map();
  for (const e of events) {
    const fecha = dayKeyInZone(e.timestamp, tz);
    if (!fecha) continue;
    if (!map.has(fecha)) map.set(fecha, { fecha, all: [], ins: [], outs: [] });
    const day = map.get(fecha);
    day.all.push(e);
    if (e.eventType === EVENT_TYPES.CLOCK_IN) day.ins.push(e.timestamp);
    else if (e.eventType === EVENT_TYPES.CLOCK_OUT) day.outs.push(e.timestamp);
  }
  return Array.from(map.values()).map((d) => {
    d.ins.sort();
    d.outs.sort();
    d.all.sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
    d.entrada = d.ins[0] || null;
    d.salida = d.outs[d.outs.length - 1] || null;
    const work = computeDayWork(d.all, { timeZone: tz });
    d.totalMinutes = work.workedMinutes;
    d.pauseMinutes = work.pauseMinutes;
    d.hasPauseWarning = work.hasPauseWarning;
    d.cancelables = d.all.filter((e) => canCancelOwnEvent(user, e));
    return d;
  }).sort((a, b) => b.fecha.localeCompare(a.fecha));
};

const fmtMinutes = (mins) => {
  if (!mins || mins <= 0) return null;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};

const hmToMinutes = (hm) => {
  if (!hm) return null;
  const [h, m] = hm.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};

// Hora de reloj en la zona del trabajador, para que hora y día cuadren.
const fmtTime = (iso, tz = DEFAULT_TIME_ZONE) => {
  if (!iso) return null;
  return formatHmInZone(iso, tz) || null;
};

const fmtDay = (key) => fmtDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' });

const Dashboard = () => {
  const { user } = useAuth();
  const toast = useToast();
  // Zona del propio trabajador (Canarias / península): días, "hoy" y horas
  // de reloj se deciden en SU hora, no en la del navegador.
  const tz = useMemo(
    () => timeZoneForWorker(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.autonomousCommunity, user?.province]
  );
  const tzHint = timeZoneLabel(tz);
  const [time, setTime] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Multicentro: centro desde el que se ficha (selector solo si hay varios) y
  // nombres de los centros del trabajador para poblarlo.
  const [workplaces, setWorkplaces] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  // Historial por día: día desplegado (detalle de fichajes) y fichaje propio
  // que se está anulando. `cancelTarget` lleva también los eventos de ese día
  // para poder avisar de lo que arrastra el borrado (salida huérfana, pausas
  // sueltas, día que vuelve a quedar sin cerrar).
  const [openDay, setOpenDay] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const canFichar = can(user?.type, 'fichar');
  const canManual = user?.canRequestCheckins ?? true;
  const overtimeAllowed = user?.overtimeAllowed ?? false;
  // Si está activo, el trabajador no puede picar antes de su hora de
  // entrada del día, INCLUSO si tiene horas extras permitidas. Las extras
  // solo se permiten después de la salida.
  const enforceStartHour = user?.enforceStartHour ?? false;
  const myWorkingSites = user?.workingSites || [];
  const multiSite = myWorkingSites.length > 1;
  const [workhoursByDow, setWorkhoursByDow] = useState(new Map());

  // Cargamos la jornada del propio usuario para saber si HOY trabaja
  // (turno mañana, tarde, partido, libre o descanso) y si el guard de hora
  // de inicio aplica. Antes esto vivía en user.startHour/endHour, pero esos
  // campos están deprecated; la fuente de verdad es /api/workhours/:id.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    getWorkHours(user.id)
      .then((slots) => {
        if (cancelled) return;
        const map = new Map();
        for (const s of (Array.isArray(slots) ? slots : [])) map.set(Number(s.dayOfWeek), s);
        setWorkhoursByDow(map);
      })
      .catch(() => { if (!cancelled) setWorkhoursByDow(new Map()); });
    return () => { cancelled = true; };
  }, [user?.id]);

  // (Antes cargábamos el workplace para validar el geofence cuando el
  // user tenía geoTracking=true. La web no valida la distancia — la app
  // móvil sí la calcula porque tiene GPS hardware —, pero SÍ captura y
  // guarda la ubicación al fichar: ver postEvent.)

  // Multicentro: inicializa el centro de fichaje al primario del trabajador.
  useEffect(() => {
    setSelectedSite(user?.primaryWorkplaceId != null ? String(user.primaryWorkplaceId)
      : (user?.workingSites?.[0] ?? null));
  }, [user?.id, user?.primaryWorkplaceId]);

  // Carga los nombres de los centros del trabajador para el selector, solo si
  // tiene más de uno (si tiene uno, no hay nada que elegir).
  useEffect(() => {
    if (!user?.companyId || (user?.workingSites?.length || 0) <= 1) { setWorkplaces([]); return; }
    let cancelled = false;
    getWorkplaces(user.companyId)
      .then((list) => { if (!cancelled) setWorkplaces(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setWorkplaces([]); });
    return () => { cancelled = true; };
  }, [user?.companyId, user?.workingSites]);

  // Slot del día actual según el calendario semanal en /api/workhours.
  // Devuelve null si el día no está configurado (= descanso).
  const todaySlot = useMemo(() => {
    const dow = jsDowToBackend(dowOfDayKey(todayInZone(tz)));
    return workhoursByDow.get(dow) || null;
  }, [workhoursByDow, tz]);

  // Hora mínima a la que el trabajador puede iniciar jornada hoy. Tiene en
  // cuenta turno mañana (in1) y tarde (in2): se permite empezar al primer
  // minuto que toque cualquiera de los dos turnos. Si no hay slot ese día
  // (descanso) no aplicamos guard.
  const todayStartMins = useMemo(() => {
    if (!todaySlot) return null;
    const parse = (hms) => {
      if (!hms) return null;
      const [h, m] = String(hms).slice(0, 5).split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const a = parse(todaySlot.in1);
    const b = parse(todaySlot.in2);
    if (a != null && b != null) return Math.min(a, b);
    return a ?? b ?? null;
  }, [todaySlot]);

  // Etiqueta corta del estado de hoy para mostrar en el header del fichaje
  // ("Día libre", "Descanso", "Turno mañana", etc.).
  const todayShiftLabel = useMemo(() => {
    if (!todaySlot) return 'Descanso';
    const has1 = todaySlot.in1 && todaySlot.out1;
    const has2 = todaySlot.in2 && todaySlot.out2;
    if (has1 && has2) return 'Turno partido';
    if (has1) return 'Turno mañana';
    if (has2) return 'Turno tarde';
    return 'Sin horario';
  }, [todaySlot]);

  const loadEvents = useCallback(async () => {
    if (!user?.id) return;
    setError(null);
    // Cubrimos los últimos ~30 días (el historial que la UI anuncia), no solo la
    // semana en curso. Así las correcciones/ediciones sobre fechas de días
    // anteriores SÍ se reflejan en el panel al recargar, y las estadísticas de
    // "últimos 30 días" cuentan lo que deben. Esta misma ventana es la que
    // acota la búsqueda de jornadas abiertas sin cerrar.
    const today = todayInZone(tz);
    const start = addDaysToDayKey(today, -EVENTS_WINDOW_DAYS);
    try {
      // Pedimos [start - 1, mañana]: el día de Madrid empieza a las
      // 22:00/23:00 UTC del anterior (sin el -1 se perderían los fichajes de
      // madrugada del primer día) y `mañana` cubre el backend ANTIGUO (fin
      // exclusivo a 00:00Z). El actual es inclusivo y devolvería también los
      // eventos de mañana (p.ej. unas vacaciones que empiezan mañana), que
      // inflarían los contadores: recortamos por día del trabajador a [start, hoy].
      const data = await getEventsByRange(user.id, addDaysToDayKey(start, -1), addDaysToDayKey(today, 1));
      setEvents(filterEventsToLocalRange(data, start, today, tz));
    } catch (err) {
      setError(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, tz]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const todayIso = todayInZone(tz, time.getTime());
  const todayEvents = useMemo(
    () => events
      .filter((e) => dayKeyInZone(e.timestamp, tz) === todayIso)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
    [events, todayIso, tz]
  );
  const lastTodayEvent = todayEvents[todayEvents.length - 1];
  const lastClockEvent = [...todayEvents].reverse().find(
    (e) => e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT
  );
  const lastPauseEvent = [...todayEvents].reverse().find(
    (e) => e.eventType === EVENT_TYPES.PAUSE_START || e.eventType === EVENT_TYPES.PAUSE_END
  );
  const fichado = lastClockEvent?.eventType === EVENT_TYPES.CLOCK_IN;
  const enPausa = fichado && lastPauseEvent?.eventType === EVENT_TYPES.PAUSE_START
    && (!lastClockEvent || new Date(lastPauseEvent.timestamp) > new Date(lastClockEvent.timestamp));
  const entrada = fichado ? new Date(lastClockEvent.timestamp) : null;

  // Jornada anterior sin cerrar (CLOCK_IN sin su CLOCK_OUT). Mientras exista,
  // al intentar fichar entrada hoy se bloquea y se abre el modal de fichaje
  // manual prefijado con esa salida pendiente: no se empieza una jornada nueva
  // sin cerrar la anterior.
  //
  // Un día sin ningún fichaje NO es incidente: simplemente no trabajó.
  //
  // Ver src/lib/openClockDays.js. Antes esto miraba solo el día anterior y
  // saltaba los días sin jornada asignada; ahora recorre toda la ventana
  // cargada. La ventana de detección va atada al rango que descarga `load()`
  // (30 días): mirar más atrás no encontraría nada porque no hay datos.
  const previousIncompleteDay = useMemo(
    () => findOldestOpenClockDay({
      events,
      clockInType: EVENT_TYPES.CLOCK_IN,
      clockOutType: EVENT_TYPES.CLOCK_OUT,
      workHoursByDow: workhoursByDow,
      lookbackDays: EVENTS_WINDOW_DAYS,
      timeZone: tz,
    }),
    [events, workhoursByDow, tz]
  );

  const postEvent = async (eventType, timestamp, isManual = false, motivo = null) => {
    setSubmitting(true);
    setError(null);
    // Ubicación (decisión 2026-09-22): si la empresa geolocaliza al trabajador
    // (geoTracking), la web la captura igual que la app, en el botón de
    // fichar Y en los fichajes manuales (en éstos, desde dónde se REGISTRA).
    // Sin ubicación no se registra nada. En web no se valida la distancia a
    // la sede (la posición del navegador puede ser por IP/Wi-Fi): se guarda
    // con su precisión (`acc`) y el admin la ve en el detalle del fichaje.
    // Formato y regla de bloqueo: src/lib/geoInfo.js (compartido con la app).
    // El motivo del fichaje manual va como token (;motivo=…) tras la ubicación.
    const mode = await resolveGeoMode(user, getMe);
    const extra = isManual ? buildGeoInfoWithMotivo(null, motivo) : null;
    let geoInfo = null;
    if (isManual || mode !== 'off') {
      const infoToast = mode === 'off' ? null : toast.info('Obteniendo tu ubicación…', { duration: 0 });
      const cap = await captureGeoInfo({
        mode,
        origin: isManual ? GEO_ORIGIN.MANUAL : GEO_ORIGIN.WEB,
        getPosition: getBrowserPosition,
        extra,
      });
      if (infoToast) toast.dismiss(infoToast);
      if (cap.blocked) {
        const msg = geoRequiredMessage(cap.error?.message, { manual: isManual });
        toast.error(msg, undefined, { title: GEO_REQUIRED_TITLE, duration: 12000 });
        setSubmitting(false);
        // Fichaje manual: el modal sigue abierto y enseña el motivo.
        if (isManual) throw new Error(msg);
        return;
      }
      geoInfo = cap.geoInfo;
    }
    const ts = timestamp || new Date().toISOString();
    const approvalStatus = isManual ? APPROVAL_STATUS.PENDING : APPROVAL_STATUS.APPROVED;
    try {
      const created = await createEvent({
        workerId: user.id,
        eventType,
        timestamp: ts,
        geoInfo,
        approvalStatus,
        // Multicentro: centro elegido (o el primario). El backend valida la geo
        // contra TODOS los centros asignados; aquí solo registramos cuál.
        workplaceId: selectedSite != null ? Number(selectedSite) : undefined,
      });
      // Confirmación con lo que ha quedado DE VERDAD: si el backend devuelve
      // el evento manda su approvalStatus; si no, el que hemos enviado.
      const pending = isPendingApproval(created?.approvalStatus != null ? created : approvalStatus);
      toast.success(isManual
        ? FEEDBACK.manualEntry({ eventType, dayKey: dayKeyInZone(ts, tz), time: formatHmInZone(ts, tz), pending })
        : FEEDBACK.clock(eventType, ts, tz));
      await loadEvents();
    } catch (err) {
      // Fichaje manual: el modal enseña el error y sigue abierto para
      // reintentar. Botón de fichar: el banner de arriba ya lo muestra.
      if (isManual) throw err;
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFichaje = () => {
    if (submitting) return;
    // Bloqueo por jornada anterior sin cerrar: si en un día previo picó entrada
    // y no cerró la salida, no puede empezar la jornada de hoy hasta resolverlo.
    // Abrimos directamente el modal de fichaje manual prefijado con la salida.
    if (!fichado && previousIncompleteDay) {
      const dLabel = fmtDayKey(previousIncompleteDay.date, { day: '2-digit', month: 'long' });
      // Si arrastra varias, se resuelven de la más antigua a la más reciente:
      // decírselo evita que piense que el bloqueo no se ha levantado.
      const rest = (previousIncompleteDay.pendingCount || 1) - 1;
      const restLabel = rest > 0
        ? ` Después te quedarán ${rest} día${rest === 1 ? '' : 's'} más por cerrar.`
        : '';
      setError(new Error(`Falta la salida del ${dLabel}. Añádela antes de fichar la entrada de hoy.${restLabel}`));
      setModalOpen(true);
      return;
    }
    // Guard de hora mínima. Aplica si:
    //   - clásico: sin diferido y sin extras → siempre se respeta el inicio.
    //   - enforceStartHour: aunque tenga overtimeAllowed=true, el flag
    //     explícito impide picar antes del inicio (las extras quedan solo
    //     para después de la salida).
    // No aplica si el día es descanso o libre (todayStartMins=null).
    const guardActive = (enforceStartHour || (!canManual && !overtimeAllowed)) && todayStartMins != null;
    if (!fichado && guardActive) {
      // Hora de reloj del trabajador: su horario está en su zona.
      const nowMins = minutesOfDayInZone(Date.now(), tz);
      if (nowMins < todayStartMins) {
        const hh = String(Math.floor(todayStartMins / 60)).padStart(2, '0');
        const mm = String(todayStartMins % 60).padStart(2, '0');
        setError(new Error(`Aún no puedes iniciar la jornada. Tu horario arranca a las ${hh}:${mm}.`));
        return;
      }
    }
    postEvent(fichado ? EVENT_TYPES.CLOCK_OUT : EVENT_TYPES.CLOCK_IN);
  };

  const togglePausa = () => {
    if (submitting || !fichado) return;
    postEvent(enPausa ? EVENT_TYPES.PAUSE_END : EVENT_TYPES.PAUSE_START);
  };

  const addManualEntry = async (entry) => {
    // Sólo se rechaza el futuro. Las horas fuera de la jornada se contabilizan como extras.
    // Fecha y hora se interpretan en la zona del trabajador (no del navegador).
    const ts = zonedDateTimeToInstant(entry.fecha, entry.hora, tz);
    if (Number.isNaN(ts.getTime())) throw new Error('Fecha u hora no válidas.');
    if (ts.getTime() > Date.now()) {
      throw new Error('No puedes registrar fichajes en el futuro. La hora aún no ha llegado.');
    }
    const TYPE_MAP = {
      entrada:     EVENT_TYPES.CLOCK_IN,
      salida:      EVENT_TYPES.CLOCK_OUT,
      pausaInicio: EVENT_TYPES.PAUSE_START,
      pausaFin:    EVENT_TYPES.PAUSE_END,
    };
    const eventType = TYPE_MAP[entry.tipo];
    if (eventType == null) throw new Error('Tipo desconocido.');
    await postEvent(eventType, ts.toISOString(), true, entry.nota || null);
  };

  // Reloj en la hora del TRABAJADOR (coherente con su historial y su horario).
  const nowParts = zonedParts(time, tz);
  const pad2 = (n) => String(n).padStart(2, '0');
  const timeStr = nowParts ? `${pad2(nowParts.hour)}:${pad2(nowParts.minute)}:${pad2(nowParts.second)}` : '';
  const dateStr = nowParts ? fmtDayKey(nowParts.dayKey, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const greeting = greetingFor(nowParts ? nowParts.hour : time.getHours());

  // Cronómetros vivos:
  //   workedMs: tiempo trabajado HOY descontando TODAS las pausas. Se congela
  //     al iniciar una pausa y vuelve a correr al reanudar.
  //   pauseMs: tiempo en la pausa ACTUAL (0 si no está en pausa).
  // Mismo cálculo que la bolsa y el resumen semanal (computeDayWork), con
  // `now` para que la entrada abierta cuente hasta este instante.
  const { workedMs, pauseMs } = useMemo(() => {
    if (!fichado) return { workedMs: 0, pauseMs: 0 };
    const work = computeDayWork(todayEvents, { timeZone: tz, now: time, unit: 'seconds' });
    const openPause = enPausa ? work.intervals.find((iv) => iv.open && iv.kind === 'pause') : null;
    return {
      workedMs: work.workedSeconds * 1000,
      pauseMs: openPause ? Math.max(0, openPause.end - openPause.start) : 0,
    };
  }, [fichado, enPausa, todayEvents, time, tz]);

  const fmtClock = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  const workedStr = fmtClock(workedMs);
  const pauseStr = fmtClock(pauseMs);

  const days = useMemo(() => groupEventsByDay(events, tz, user), [events, tz, user]);

  const cancelWarning = cancelTarget
    ? cancelWarningText(cancelTarget.event, cancelTarget.dayEvents)
    : '';

  // Anula (borra) un fichaje PENDIENTE del propio trabajador. La regla se
  // vuelve a comprobar aquí: entre pintar el botón y pulsarlo, el fichaje ha
  // podido aprobarse desde otra pestaña.
  const confirmCancelOwnEvent = async () => {
    const target = cancelTarget;
    if (!target || cancelling) return;
    if (!canCancelOwnEvent(user, target.event)) {
      throw new Error('Este fichaje ya no se puede anular. Vuelve a cargar la página.');
    }
    setCancelling(true);
    let failure = null;
    try {
      await deleteEvent(target.event.id);
      toast.success(FEEDBACK.ownEventCancelled({
        eventType: target.event.eventType,
        dayKey: dayKeyInZone(target.event.timestamp, tz),
        time: formatHmInZone(target.event.timestamp, tz),
      }));
    } catch (err) {
      // 404: ya estaba borrado (otra pestaña, o el admin desde Aprobaciones).
      // No es nada que el trabajador tenga que resolver: se avisa y se refresca.
      if (err?.status === 404) toast.info(FEEDBACK.ownEventAlreadyGone);
      else failure = err;
    }
    setCancelling(false);
    // Pase lo que pase se recarga: los totales del día y el estado
    // abierto/cerrado salen de lo que hay DE VERDAD en el servidor (quien
    // borra una salida pendiente tiene que ver el día "En curso" otra vez).
    await loadEvents();
    // Con error de verdad, ConfirmDialog lo enseña y se queda abierto.
    if (failure) throw failure;
  };

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1 capitalize">
            Inicio · {dateStr}
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">
            {greeting}, {user?.nombre || 'Usuario'}.
          </h1>
        </div>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-ink">{error.message}</div>
            {error.url && <div className="text-xs text-ink-muted font-mono">{error.url}</div>}
          </div>
        </div>
      )}

      {canFichar ? (
        <section className="relative overflow-hidden rounded-2xl border border-primary-light bg-gradient-to-br from-primary-soft via-white to-accent-light/40 p-6 md:p-8 mb-6 shadow-sm">
          <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-primary to-accent" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pl-2">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold mb-2">
                {fichado ? (
                  enPausa ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-warning" />
                      <span className="text-warning">En pausa</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                      <span className="text-success">Jornada activa</span>
                    </>
                  )
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-ink-muted" />
                    <span className="text-ink-muted">Fuera de jornada</span>
                  </>
                )}
                {/* Chip del estado de la jornada de hoy según workhours */}
                <span
                  className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    todayShiftLabel === 'Descanso'        ? 'bg-slate-200 text-ink-muted' :
                    todayShiftLabel === 'Horario libre'   ? 'bg-accent text-white' :
                    todayShiftLabel === 'Turno mañana'    ? 'bg-amber-100 text-amber-800' :
                    todayShiftLabel === 'Turno tarde'     ? 'bg-indigo-100 text-indigo-800' :
                    todayShiftLabel === 'Turno partido'   ? 'bg-primary-soft text-primary-darker' :
                    'bg-slate-100 text-ink-muted'
                  }`}
                  title={todaySlot
                    ? `Tu horario hoy: ${[
                        todaySlot.in1 && todaySlot.out1 ? `${String(todaySlot.in1).slice(0,5)}–${String(todaySlot.out1).slice(0,5)}` : null,
                        todaySlot.in2 && todaySlot.out2 ? `${String(todaySlot.in2).slice(0,5)}–${String(todaySlot.out2).slice(0,5)}` : null,
                      ].filter(Boolean).join(' · ')}`
                    : 'Hoy no trabajas según tu jornada. Si fichas, queda pendiente y el admin decide si son horas extras o compensación.'}
                >
                  {todayShiftLabel}
                </span>
              </div>
              <div className="font-display text-6xl md:text-7xl font-bold tabular-nums text-ink leading-none">
                {timeStr}
              </div>
              {tzHint && <div className="mt-1 text-[11px] text-ink-muted">{tzHint}</div>}
              <div className="mt-3 text-sm space-y-1">
                {fichado ? (
                  <>
                    <div className="text-ink">
                      Llevas{' '}
                      <span className={`font-bold tabular-nums ${enPausa ? 'text-ink-muted' : 'text-primary-darker'}`}>
                        {workedStr}
                      </span>{' '}
                      trabajadas{enPausa && <span className="text-ink-muted"> (en pausa)</span>}
                    </div>
                    {enPausa && (
                      <div className="text-ink">
                        Llevas{' '}
                        <span className="font-bold tabular-nums text-warning">{pauseStr}</span>{' '}
                        de pausa
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-ink-muted">Ficha tu entrada para empezar la jornada</span>
                )}
              </div>
              {todayShiftLabel === 'Turno partido' && (
                <div className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-ink-muted bg-primary-soft border border-primary-light rounded-lg px-2.5 py-1.5">
                  <Coffee size={12} className="text-primary mt-0.5 shrink-0" />
                  <span>
                    Hoy tienes turno partido. Al terminar el turno de mañana ficha <b className="text-ink">Salida</b>,
                    y al volver por la tarde ficha <b className="text-ink">Entrada</b> de nuevo. Cierra la jornada con
                    una segunda salida al acabar.
                  </span>
                </div>
              )}
            </div>

            {/* Botones de acción. flex-wrap para que en pantallas estrechas
                el último o los últimos botones pasen a una segunda fila sin
                desbordar el card. En desktop (lg+) hay sitio sobrado y van
                todos en línea sin envolver. */}
            <div className="flex flex-wrap gap-2 lg:flex-nowrap w-full md:w-auto md:min-w-0">
              {multiSite && (
                <select
                  value={selectedSite ?? ''}
                  onChange={(e) => setSelectedSite(e.target.value || null)}
                  disabled={submitting}
                  title="Centro desde el que fichas"
                  className="flex-1 md:flex-none px-3 py-3 rounded-xl font-semibold text-sm text-ink bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-60"
                >
                  {myWorkingSites.map((sid) => {
                    const wp = workplaces.find((w) => String(w.id) === String(sid));
                    return <option key={sid} value={sid}>{wp ? wp.name : `Sede #${sid}`}</option>;
                  })}
                </select>
              )}
              <button
                onClick={toggleFichaje}
                disabled={submitting}
                className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md disabled:opacity-60 ${
                  fichado
                    ? 'bg-danger text-white hover:bg-red-700 hover:shadow-lg'
                    : 'bg-primary text-white hover:bg-primary-dark hover:shadow-lg'
                }`}
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> :
                  fichado ? <><Square size={18} /> Fichar salida</> : <><Play size={18} /> Fichar entrada</>}
              </button>
              {fichado && (
                <button
                  onClick={togglePausa}
                  disabled={submitting}
                  className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-60 ${
                    enPausa ? 'bg-success text-white hover:bg-emerald-700' : 'bg-warning text-white hover:bg-amber-700'
                  }`}
                  title={enPausa ? 'Reanudar trabajo' : 'Iniciar pausa'}
                >
                  <Coffee size={16} /> {enPausa ? 'Reanudar' : 'Pausa'}
                </button>
              )}
              {canManual && (
                <button
                  onClick={() => setModalOpen(true)}
                  disabled={submitting}
                  className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-ink-muted bg-white border border-slate-200 hover:border-slate-300 hover:text-ink transition-colors disabled:opacity-60"
                  title="Añadir fichaje manual en diferido"
                >
                  <Plus size={16} /> Manual
                </button>
              )}
              <button
                onClick={() => setCorrectionOpen(true)}
                disabled={submitting}
                className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-ink-muted bg-white border border-slate-200 hover:border-slate-300 hover:text-ink transition-colors disabled:opacity-60"
                title="Solicitar corrección de un fichaje ya registrado"
              >
                <Wrench size={16} /> Corregir
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 text-center">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">Solo consulta</div>
          <div className="text-ink">Tu rol no permite fichar entradas ni salidas.</div>
        </section>
      )}

      <section className="pb-5 mb-5 border-b border-slate-200">
        <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-3 flex items-center gap-2">
          <TrendingUp size={14} className="text-primary" /> Resumen
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Clock size={11} className="text-primary" /> Días con jornada
            </div>
            <div className="font-display text-2xl font-bold text-primary-darker tabular-nums">
              {days.filter((d) => d.entrada).length}
            </div>
            <div className="text-xs text-ink-muted mt-0.5">últimos 30 días</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Calendar size={11} className="text-success" /> Jornadas completas
            </div>
            <div className="font-display text-2xl font-bold text-success tabular-nums">
              {days.filter((d) => d.entrada && d.salida).length}
            </div>
            <div className="text-xs text-ink-muted mt-0.5">con entrada y salida</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Clock size={11} className="text-warning" /> Registros
            </div>
            <div className="font-display text-2xl font-bold text-warning tabular-nums">{events.length}</div>
            <div className="text-xs text-ink-muted mt-0.5">eventos totales</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
              <Plane size={11} className="text-accent-dark" /> Vacaciones
            </div>
            <div className="font-display text-2xl font-bold text-accent-dark tabular-nums">
              {events.filter((e) => e.eventType === EVENT_TYPES.VACATION_START).length}
            </div>
            <div className="text-xs text-ink-muted mt-0.5">solicitudes</div>
          </div>
        </div>
      </section>

      <section className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Historial por día</div>
          <span className="text-xs text-ink-muted">
            {tzHint && <>{tzHint} · </>}{days.length} jornadas
          </span>
        </div>
        <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
              <Loader2 size={18} className="animate-spin text-primary" />
              <span className="text-sm">Cargando historial…</span>
            </div>
          ) : days.length === 0 ? (
            <div className="py-10 text-center text-sm text-ink-muted">
              No hay jornadas registradas en los últimos 30 días.
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr className="text-xs uppercase tracking-wider text-ink-muted">
                  <th className="text-left font-semibold py-3 pr-4">Día</th>
                  <th className="text-left font-semibold py-3 pr-4">Entrada</th>
                  <th className="text-left font-semibold py-3 pr-4">Salida</th>
                  <th className="text-left font-semibold py-3 pr-4">Pausa</th>
                  <th className="text-right font-semibold py-3 pr-4">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {days.map((d) => {
                  const total = fmtMinutes(d.totalMinutes);
                  const pausa = fmtMinutes(d.pauseMinutes);
                  const expanded = openDay === d.fecha;
                  const cancelableCount = d.cancelables.length;
                  return (
                    <React.Fragment key={d.fecha}>
                      <tr
                        className="border-t border-slate-100 hover:bg-white transition-colors cursor-pointer"
                        onClick={() => setOpenDay(expanded ? null : d.fecha)}
                      >
                        <td className="py-2.5 pr-4 capitalize font-medium text-ink">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-ink-muted">
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            {fmtDay(d.fecha)}
                            {cancelableCount > 0 && (
                              <span
                                className="normal-case px-1.5 py-0.5 rounded-full bg-warning/10 text-warning text-[10px] font-bold"
                                title="Tienes fichajes pendientes de aprobación en este día. Puedes anularlos."
                              >
                                {cancelableCount} pendiente{cancelableCount === 1 ? '' : 's'}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums text-success font-semibold">
                          {fmtTime(d.entrada, tz) || <span className="text-ink-muted font-normal">—</span>}
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums text-primary-darker font-semibold">
                          {fmtTime(d.salida, tz) || <span className="text-ink-muted font-normal">—</span>}
                        </td>
                        <td className="py-2.5 pr-4 tabular-nums text-warning font-medium">
                          {pausa || <span className="text-ink-muted font-normal">—</span>}
                          {d.hasPauseWarning && (
                            <span
                              className="ml-1.5 inline-flex items-center align-middle text-warning"
                              title="Pausa sin cerrar o sin inicio: revisa los fichajes de este día"
                            >
                              <AlertTriangle size={12} />
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums font-bold text-ink">
                          {total || (d.entrada ? <span className="text-warning font-medium">En curso</span> : '—')}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-t border-slate-100 bg-white">
                          <td colSpan={5} className="py-3 pl-6 pr-4">
                            <div className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold mb-2">
                              Fichajes del día
                            </div>
                            <ul className="space-y-1">
                              {d.all.map((e) => {
                                const pendiente = isPendingApproval(e);
                                const rechazado = Number(e.approvalStatus) === APPROVAL_STATUS.REJECTED;
                                const cancelable = canCancelOwnEvent(user, e);
                                return (
                                  <li key={e.id} className="flex items-center gap-2 text-sm">
                                    <span className="tabular-nums text-ink-muted w-12 shrink-0">
                                      {fmtTime(e.timestamp, tz) || '—'}
                                    </span>
                                    <span className="text-ink">
                                      {EVENT_TYPE_LABELS[e.eventType] || `Tipo ${e.eventType}`}
                                    </span>
                                    {pendiente && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-warning/10 text-warning text-[10px] font-bold">
                                        Pendiente de aprobación
                                      </span>
                                    )}
                                    {rechazado && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-danger/10 text-danger text-[10px] font-bold">
                                        Rechazado
                                      </span>
                                    )}
                                    {cancelable && (
                                      <button
                                        type="button"
                                        onClick={() => setCancelTarget({ event: e, dayEvents: d.all })}
                                        disabled={cancelling}
                                        className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
                                        title="Anular esta solicitud pendiente"
                                      >
                                        <Trash2 size={13} /> Anular
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                            <div className="mt-2 text-[11px] text-ink-muted">
                              Solo puedes anular tus entradas y salidas PENDIENTES de aprobación. Para
                              cambiar una ya aprobada, usa <b className="text-ink">Corregir</b>.
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <ManualEntryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={addManualEntry}
        timeZone={tz}
        initialValues={previousIncompleteDay ? {
          tipo: 'salida',
          fecha: previousIncompleteDay.date,
          hora: previousIncompleteDay.suggestedTime,
        } : null}
        notice={previousIncompleteDay ? `Te falta la salida del ${fmtDayKey(previousIncompleteDay.date, { day: '2-digit', month: 'long' })}. Añádela para poder fichar la entrada de hoy.` : null}
      />

      <CorrectionRequestModal
        open={correctionOpen}
        onClose={() => setCorrectionOpen(false)}
        workerId={user?.id}
        worker={user}
        onSubmitted={loadEvents}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        title="¿Anular esta solicitud?"
        message={cancelTarget
          ? `Se eliminará tu ${eventNoun(cancelTarget.event.eventType)} del `
            + `${fmtDayKey(dayKeyInZone(cancelTarget.event.timestamp, tz), { day: '2-digit', month: 'long' })}`
            + `${formatHmInZone(cancelTarget.event.timestamp, tz) ? ` a las ${formatHmInZone(cancelTarget.event.timestamp, tz)}` : ''}. `
            + 'Todavía está pendiente de aprobación, así que no queda registrada en tu jornada.'
          : ''}
        warning={cancelWarning || null}
        confirmText="Anular fichaje"
        cancelText="Cancelar"
        onClose={() => { if (!cancelling) setCancelTarget(null); }}
        onConfirm={confirmCancelOwnEvent}
      />
    </div>
  );
};

export default Dashboard;
